import { observer } from 'mobx-react-lite'
import { Box, IconButton, Typography } from 'shared/ui'
import { useShortcutTooltip } from 'shared/ui/ShortcutTooltip/useShortcutTooltip'
import { useChipsClick } from 'shared/hooks/useChipsClick.ts'
import { EnhancePrompt } from 'entities/AIAgent/actions/enhancePrompt/EnhancePrompt'
import {
  addConversationNoteTemplate,
  addContactNoteTemplate,
  addTagTemplate,
  handoffToHumanTemplate,
  type IMessageActionData,
  type ITagActionData,
  removeTagTemplate,
  sendSMSTemplate,
} from 'entities/TemplateActions/actions.tsx'
import { handoffToHuman } from 'widgets/TemplateActions/handoffToHuman'
import { MessageField } from 'widgets/MessageField'
import { addTag } from 'widgets/TemplateActions/addTag'
import { removeTag } from 'widgets/TemplateActions/removeTag'
import { addContactNote } from 'widgets/TemplateActions/addContactNote'
import { sendSMS } from 'widgets/TemplateActions/sendSMS/sendSMS.tsx'
import { addConversationNote } from 'widgets/TemplateActions/addConversationNote'
import { ShortcutMenu } from './ui/ShortcutMenu/ShortcutMenu'
import { type InstructionsSectionStore } from './store/instructionsSectionStore'

export type IInstructionsSectionContentProps = {
  store: InstructionsSectionStore
  isExpanded?: boolean
  onOpenModal?: () => void
}

export const InstructionsSectionContent = observer(
  ({ store, isExpanded, onOpenModal }: IInstructionsSectionContentProps) => {
    const {
      entities,
      messageFieldStore,
      instructions,
      error,
      setError,
      setInstructions,
      requestEnhance,
      mergeFieldConfig,
      onUpdateHandoffToHumanAction,
      deleteHandoffToHumanAction,
      getHandoffNotifySettings,
      deleteAction,
      onUpdateTagAction,
      onUpdateRemoveTagAction,
      onUpdateContactNoteAction,
      onUpdateSendSMSAction,
      onUpdateConversationNoteAction,
    } = store
    const { replaceTriggerContent, updateTriggerContent, textFieldElement } = messageFieldStore

    const { triggerTooltip, infos } = useShortcutTooltip({
      textFieldElement,
      actions: [
        {
          shortcut: '/',
          infoLabel: 'to add contact fields and actions',
          renderComponent: (onClose) => (
            <ShortcutMenu onClose={onClose} mergeFieldConfig={mergeFieldConfig} store={store} />
          ),
        },
      ],
    })

    useChipsClick({
      textFieldElement,
      replaceTriggerContent,
      updateTriggerContent,
      actions: {
        [addTagTemplate.regex]: (chip: HTMLSpanElement, data: ITagActionData) =>
          'tags' in data
            ? addTag({
                onDelete: () => deleteAction(chip),
                onSave: (data) => onUpdateTagAction(chip, data),
                data,
              })
            : undefined,
        [removeTagTemplate.regex]: (chip: HTMLSpanElement, data: ITagActionData) =>
          'tags' in data
            ? removeTag({
                onDelete: () => deleteAction(chip),
                onSave: (data) => onUpdateRemoveTagAction(chip, data),
                data,
              })
            : undefined,
        [addContactNoteTemplate.regex]: (chip: HTMLSpanElement, data: IMessageActionData) =>
          addContactNote(
            {
              onDelete: () => deleteAction(chip),
              onSave: (data) => onUpdateContactNoteAction(chip, data),
              data: {
                auto: 'auto' in data ? data.auto : false,
                message: 'content' in data ? data.content : '',
                media_url: [],
              },
            },
            entities
          ),
        [addConversationNoteTemplate.regex]: (chip: HTMLSpanElement, data: IMessageActionData) =>
          addConversationNote(
            {
              onDelete: () => deleteAction(chip),
              onSave: (data) => onUpdateConversationNoteAction(chip, data),
              data: {
                auto: 'auto' in data ? data.auto : false,
                message: 'content' in data ? data.content : '',
                media_url: [],
              },
              shouldHideAttachment: true,
            },
            entities
          ),
        [sendSMSTemplate.regex]: (chip: HTMLSpanElement, data: IMessageActionData) =>
          sendSMS(
            {
              onDelete: () => deleteAction(chip),
              onSave: (data) => onUpdateSendSMSAction(chip, data),
              data: {
                auto: 'auto' in data ? data.auto : false,
                message: 'content' in data ? data.content : '',
                media_url: [],
              },
            },
            entities
          ),
        [handoffToHumanTemplate.regex]: (chip) =>
          handoffToHuman({
            onSave: (data) => onUpdateHandoffToHumanAction(data),
            onDelete: () => deleteHandoffToHumanAction(chip),
            getData: getHandoffNotifySettings,
          }),
      },
    })

    return (
      <>
        {triggerTooltip}
        <Box
          display={'flex'}
          alignItems={'flex-start'}
          justifyContent={'space-between'}
          marginBottom={'24px'}
        >
          <Box display={'flex'} flexDirection={'column'}>
            <Typography
              variant={isExpanded ? 'heading-lg' : 'body-lg-medium'}
              ariaLabel={'Instructions'}
            >
              Instructions
            </Typography>
            {infos}
          </Box>
          <Box display={'flex'} alignItems={'center'} gap={'2px'}>
            <EnhancePrompt
              prompt={instructions}
              onAcceptPrompt={setInstructions}
              setError={setError}
              requestEnhance={requestEnhance}
            />
            {!isExpanded && (
              <IconButton
                iconProps={{ fontSize: 16 }}
                variant={'icon'}
                color={'tertiary'}
                size={'small'}
                icon={'solidExpand'}
                ariaLabel='Expand'
                onClick={onOpenModal}
                tooltipProps={{
                  label: 'Expand',
                  placement: 'top',
                }}
              />
            )}
          </Box>
        </Box>
        <MessageField messageFieldStore={messageFieldStore} uiStyle={'new'} error={error} />
      </>
    )
  }
)
